<template>
	<Header
		title="Users"
		:is-loading="loaderEnabled && listState.isLoading.value" />

	<Container>
		<TableFilters
			placeholder="Search users..."
			:controls="controls"
			:filters="filters" />
	</Container>

	<Container class="users-list">
		<ApiTable
			clickable
			:list-state="listState"
			@row-click="navigateToUser">
			<Column header="User">
				<template #body="{ data }">
					<div class="users-list__user">
						<span class="users-list__user-email">{{ data.email }}</span>
						<span class="users-list__user-id">#{{ data.id }}</span>
					</div>
				</template>
			</Column>
			<Column
				header="Role"
				header-style="width: 120px">
				<template #body="{ data }">
					<Tag
						:severity="data.role === 'admin' ? 'warn' : 'info'"
						:value="data.role" />
				</template>
			</Column>
			<Column
				header="Tokens"
				header-style="width: 140px">
				<template #body="{ data }">
					<span>{{ data.tokens_remaining }}</span>
				</template>
			</Column>
			<Column
				header="Verified"
				header-style="width: 120px">
				<template #body="{ data }">
					<Tag
						:severity="data.verified ? 'success' : 'secondary'"
						:value="data.verified ? 'Yes' : 'No'" />
				</template>
			</Column>
			<Column
				header="Created"
				header-style="width: 200px">
				<template #body="{ data }">
					{{ formatHuman(data.created_at) }}
				</template>
			</Column>
		</ApiTable>
	</Container>
</template>

<script setup lang="ts">
import Header from '@/components/Header.vue'
import Container from '@/components/Container.vue'
import Tag from 'primevue/tag'
import useApiTable from '@/components/Table/useApiTable'
import { useUserListState } from '@/models/User/States'
import TableFilters from '@/components/filters/TableFilters.vue'
import { FilterControls } from '@/helpers/filters/FilterControls'
import { Filters } from '@/helpers/filters/Filters'
import { ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { format } from 'date-fns'

const listState = useUserListState()
listState.defaultParams = {
	orderBy: 'created_at',
	order: 'desc',
	per_page: 20,
}

const { ApiTable, Column } = useApiTable(listState)
const controls = new FilterControls([])
const route = useRoute()
const router = useRouter()
const filters = ref(Filters.fromQuery(route.query as any, controls))
const loaderEnabled = ref(true)

watch(
	() => route.query.s,
	async (val) => {
		loaderEnabled.value = true
		filters.value.search = typeof val === 'string' ? val : ''
		listState.defaultParams.search = filters.value.search
		await listState.getList()
	},
	{ immediate: true },
)

watch(
	() => filters.value.search,
	(val, prev) => {
		if (val === prev) return
		const q = { ...route.query }
		if (!val) delete (q as any).s
		else (q as any).s = val
		router.replace({ query: q })
	},
)

watchEffect(() => {
	if (listState.isLoaded.value && !listState.isLoading.value) loaderEnabled.value = false
})

function formatHuman(date: string) {
	return format(new Date(date), 'PPpp')
}

function navigateToUser(event: any) {
	router.push({ name: 'admin-users-details', params: { id: event.data.id } })
}
</script>

<style lang="scss" scoped>
.users-list {
	display: flex;
	justify-content: center;
	padding-top: 0;

	.users-list__user {
		display: flex;
		flex-direction: column;
		gap: 2px;

		.users-list__user-email {
			font-weight: 600;
		}

		.users-list__user-id {
			font-size: 11px;
			color: var(--text-color);
			opacity: 0.8;
			font-family: monospace;
		}
	}
}
</style>
